import React, {
	createRef,
	Component,
	type ForwardedRef,
	type HTMLAttributes,
	forwardRef,
	useState,
	useLayoutEffect,
} from 'react';
import throttle from 'lodash/throttle';
import * as R from 'ramda';
import FocusLock from 'react-focus-lock';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import { Toolbar, type ToolbarItem } from '@atlassian/jira-aais-timeline-toolbar';
import { ViewExperienceTrackingProvider } from '@atlassian/jira-common-experience-tracking-viewing';
import { ReportErrors } from '@atlassian/jira-errors-handling';
import { ff } from '@atlassian/jira-feature-flagging';
import { fg } from '@atlassian/jira-feature-gating';
import { FlagGroup } from '@atlassian/jira-flags';
import FullscreenButton from '@atlassian/jira-fullscreen-button';
import { JiraBottomRightCornerOutlet } from '@atlassian/jira-layout-controller';
import {
	Sections,
	type SectionRendererArgs,
	type SectionsData,
	type SectionData,
	VIEW_MODES,
} from '@atlassian/jira-portfolio-3-common';
import {
	HorizontalSyncedScrollbar,
	HorizontalSyncedScrollView,
} from '@atlassian/jira-portfolio-3-horizontal-scrolling-synced';
import { UseShortcuts } from '@atlassian/jira-portfolio-3-keyboard-shortcuts/src/controllers';
import { CttSpotlights } from '@atlassian/jira-portfolio-3-onboarding/src/controllers/ctt-spotlights';
import { Spotlights } from '@atlassian/jira-portfolio-3-onboarding/src/controllers/spotlights';
import { SpotlightTarget as OnboardingSpotlightTarget } from '@atlassian/jira-portfolio-3-onboarding/src/ui/spotlight-target';
import TmpCrossProjectChangeBoarding from '@atlassian/jira-portfolio-3-onboarding/src/ui/tmp-cross-project-parenting';
import { isPollinatorTenant } from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/feature-flags';
import ViewExperienceFailureTracker from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/view/failure-tracker';
import IssueModal from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/view/main/tabs/roadmap/scope/issues/issue-modal';
import { getViewportSize } from '@atlassian/jira-portfolio-3-portfolio/src/common/dom';
import { isDefined } from '@atlassian/jira-portfolio-3-portfolio/src/common/ramda';
import {
	ERROR_REPORTING_PACKAGE,
	ERROR_REPORTING_TEAM,
	PACKAGE_NAME,
	STICKY_HEADER_CONTAINER_PREFIX,
	SECTION_KEYS,
	SECTION_ITEM_KEYS,
} from '@atlassian/jira-portfolio-3-portfolio/src/common/view/constant';
import { getNonTimelineWidth } from '@atlassian/jira-portfolio-3-portfolio/src/common/window';
import {
	ContextualAnalyticsData,
	MountEvent,
	SCREEN,
} from '@atlassian/jira-product-analytics-bridge';
import { JIRA_PORTFOLIO } from '@atlassian/jira-shared-types';
import UFOSegment from '@atlassian/jira-ufo-segment';
import UFOCustomData from '@atlassian/ufo-custom-data';
import ConfiguredDatesFlag from './configured-dates-flag';
import { DependenciesFlyoutProvider, DependenciesFlyoutOverlay } from './dependencies-flyout';
import RatingFeedbackCollectorAsync from './feedback-collector/async';
import Fields from './fields';
import FieldsHeader from './fields/header';
import {
	ZoomLevelSync,
	HorizontalScrollBar,
	HorizontalScrollBarOverlay,
} from './horizontal-scrolling';
import Legend from './legend';
import PanningControl from './panning-control';
import AllIssuesSelect from './scope/all-issues-select';
import ScopeHeader from './scope/header';
import Scope from './scope/issues';
import StickyHeaderCell from './sticky-header/cell';
import StickyHeaderFields from './sticky-header/fields-header';
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-global-styles -- Ignored via go/DSP-18766
import * as styles from './styles.module.css';
import TimeScale from './time-scale';
import Timeline from './timeline';
import TimelineHeader from './timeline/header';
import TodayButton from './today-button';
import type { Props, State } from './types';
import UseLocalStorageCommand from './use-local-storage-command';
import {
	getSectionsData,
	getHighlightRowId,
	toggleRowHighlighter,
	shouldSetOnboardingWidth,
	getExperienceName,
} from './util';

const MIN_APP_WIDTH = 1000; // cssVariables['jpo-common-min-app-width'];

const LEFT_MARGIN_DEFAULT = 40;
const LEFT_MARGIN_REPORT = 24;

const ContentScroller = forwardRef(
	(
		{
			children,
			...otherProps
		}: HTMLAttributes<HTMLDivElement> & {
			'data-scroll': 'lockable';
		},
		ref: ForwardedRef<HTMLDivElement>,
	) => {
		const [mounted, setMounted] = useState(false);

		useLayoutEffect(() => {
			// We only want to mount the child component (issues) after the scroll container has been mounted because
			// the child component requires a reference to the scroll container. useLayoutEffect delays when we
			// actually mount the child component.
			setMounted(true);
		}, []);

		return (
			<div {...otherProps} ref={ref}>
				{mounted && children}
			</div>
		);
	},
);

// eslint-disable-next-line jira/react/no-class-components
class Roadmap extends Component<Props, State> {
	// eslint-disable-next-line @typescript-eslint/no-explicit-any, react/sort-comp
	containerRef = createRef<any>();

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	providerRef = createRef<any>();

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	sectionsRef = createRef<any>();

	scrollTopRef = createRef<number>();

	sectionsContainerRef = createRef<HTMLDivElement>();

	navigationApp: HTMLElement | null = null;

	navigationAppObserver: MutationObserver | null = null;

	static defaultProps = {
		ZoomLevelSync,
		TimeScale,
		PanningControl,
		Scope,
		ScopeHeader,
		Fields,
		FieldsHeader,
		Timeline,
		TimelineHeader,
		AllIssuesSelect,
		IssueModal,
		ViewExperienceFailureTracker,
	};

	state = {
		containerWidth: 0,
		timelineSpotlightTopPosition: 68,
		timelineFullScreen: false,
		dividerVisible: true,
	};

	scrollElement: HTMLDivElement | null = null;

	activeRow: string | null | undefined = null;

	scopeColumnWidth = 0;

	fieldsColumnWidth = 0;

	timelineColumnWidth = 0;

	// Fields viewport width
	fieldsViewportWidth = 0;

	getContainerRef() {
		return this.props.containerRef || this.containerRef;
	}

	// eslint-disable-next-line react/sort-comp
	constructor(props: Props) {
		super(props);
		if (props.isExportMode) {
			this.state.containerWidth = getNonTimelineWidth() + props.exportModeTimelineWidth;
		}
	}

	componentDidMount() {
		const ref = this.getContainerRef();
		if (ref.current) {
			const containerEl = ref.current;
			const containerTop = containerEl.getBoundingClientRect().top;
			if (containerTop !== this.state.timelineSpotlightTopPosition) {
				this.setState({
					timelineSpotlightTopPosition: containerTop + 68,
				});
			}
		}

		if (this.scrollElement) this.setState({ dividerVisible: this.scrollElement.scrollTop > 0 });

		this.navigationApp = document.getElementById('navigation-app');
		if (this.navigationApp) {
			this.navigationAppObserver = new MutationObserver(this.measureAndUpdateWidthThrottled);
			const config = { attributes: true, subtree: true };
			this.navigationAppObserver.observe(this.navigationApp, config);
		}

		if (!this.props.isExportMode) {
			this.measureAndUpdateWidth();
			window.addEventListener('resize', this.measureAndUpdateWidthThrottled);
		}
	}

	shouldComponentUpdate(nextProps: Props, nextState: State) {
		return !(R.equals(nextProps, this.props) && R.equals(nextState, this.state));
	}

	componentDidUpdate(prevProps: Props) {
		if (
			this.props.rightSidebarWidth !== prevProps.rightSidebarWidth ||
			this.props.leftSidebarWidth !== prevProps.leftSidebarWidth
		) {
			this.measureAndUpdateWidthThrottled();
		}

		if (
			this.props.appWidth !== prevProps.appWidth &&
			this.props.isExportMode &&
			this.props.appWidth !== this.state.containerWidth
		) {
			this.setState({
				containerWidth: this.props.appWidth,
			});
		}

		// These event handlers are added to highlight the rows on mouse over.
		const sectionsContainer = this.sectionsContainerRef.current;
		if (sectionsContainer instanceof HTMLElement) {
			sectionsContainer.addEventListener('mouseover', this.onMouseOver);
			sectionsContainer.addEventListener('mouseout', this.onMouseOut);
		}

		const {
			isExportMode,
			isLoading,
			onLoadComplete,
			reportTimeToInteractive,
			isGoalsLoadingOld,
			isGoalsLoadingNew,
			isIdeasLoading,
		} = this.props;

		const isGoalsLoading = fg('plan_timeline_sort_goal_groups_alphabetically')
			? isGoalsLoadingNew
			: isGoalsLoadingOld;

		if (!isLoading && !isGoalsLoading && !isIdeasLoading && onLoadComplete) {
			onLoadComplete();
		}

		if (!isExportMode && !isLoading) {
			reportTimeToInteractive({
				isNewSidebarEnabled: true,
			});
		}
	}

	componentWillUnmount() {
		if (this.navigationAppObserver) {
			this.navigationAppObserver.disconnect();
		}
		if (!this.props.isExportMode) {
			window.removeEventListener('resize', this.measureAndUpdateWidthThrottled);
		}

		// The event handlers to highlight the rows on mouse over are removed on unmount.
		const sectionsContainer = this.sectionsContainerRef.current;
		if (sectionsContainer instanceof HTMLElement) {
			sectionsContainer.removeEventListener('mouseover', this.onMouseOver);
			sectionsContainer.removeEventListener('mouseout', this.onMouseOut);
		}
	}

	/**
	 * Manage row highlighting.
	 * Basic idea is to find the closest row highlighter element and switch on all row highlighters
	 * with the same id (and switch off previously active ones). Highlighter id is issueId at the
	 * moment, take it into account when extending functionality to non-issue rows so there will be
	 * no id overlap or instability.
	 */
	onMouseOver = (e: Event) => {
		let parent = e.target;
		// Using while + parentNode as Element.closest() is not supported by IE11.
		while (parent instanceof Node) {
			if (parent instanceof HTMLElement) {
				const { issue, group } = parent.dataset;
				const highlightRowId = getHighlightRowId(group, issue);
				if (issue && highlightRowId !== this.activeRow) {
					if (isDefined(this.activeRow)) {
						toggleRowHighlighter(this.activeRow, false);
					}
					this.activeRow = highlightRowId;
					toggleRowHighlighter(this.activeRow, true);
					return;
				}
			}
			parent = parent.parentNode;
		}
	};

	onMouseOut = () => {
		if (isDefined(this.activeRow)) {
			toggleRowHighlighter(this.activeRow, false);
		}

		this.activeRow = null;
	};

	handleScroll = () => {
		if (this.scrollElement) {
			const scrollTop = this.scrollElement.scrollTop;

			this.setState({ dividerVisible: scrollTop > 0 });

			if (!isDefined(this.props.setStickyHeaderScroll)) return;
			if (this.scrollTopRef.current === scrollTop) return;

			// @ts-expect-error - TS2722 - Cannot invoke an object which is possibly 'undefined'. | TS2540 - Cannot assign to 'current' because it is a read-only property.
			this.scrollTopRef.current = scrollTop;
			this.props.setStickyHeaderScroll(scrollTop);
		}
	};

	handleSectionsRender = (sectionsData: SectionsData) => {
		const sectionsById = R.indexBy(R.prop('id'), sectionsData);
		const scopeWidth = sectionsById[SECTION_ITEM_KEYS.SCOPE].width || this.scopeColumnWidth;
		const fieldsWidth = sectionsById[SECTION_ITEM_KEYS.FIELDS].width || this.fieldsColumnWidth;
		const timelineWidth =
			sectionsById[SECTION_ITEM_KEYS.TIMELINE]?.width || this.timelineColumnWidth;
		const fieldsViewportWidth = Math.max(this.state.containerWidth - scopeWidth, 0);

		if (fieldsViewportWidth !== this.fieldsViewportWidth) {
			this.fieldsViewportWidth = fieldsViewportWidth;
		}

		if (
			scopeWidth !== this.scopeColumnWidth ||
			fieldsWidth !== this.fieldsColumnWidth ||
			timelineWidth !== this.timelineColumnWidth
		) {
			this.scopeColumnWidth = scopeWidth;
			this.fieldsColumnWidth = fieldsWidth;
			this.timelineColumnWidth = timelineWidth;
			/**
			 * This update the section-widths in the redux state which will be consumed elsewhere i.e. PngExport
			 * Ideally it should not be called in "render" lifecycle but this is where we could access the section widths
			 */
			this.props.updateTopLevelColumnWidths({
				scope: this.scopeColumnWidth,
				fields: this.fieldsColumnWidth,
				timeline: this.timelineColumnWidth,
			});
		}
	};

	toggleFullscreen = (goFullScreen: boolean) => {
		this.setState({ timelineFullScreen: !goFullScreen }, () => {
			this.measureAndUpdateWidth();
		});
	};

	expandFieldsShortcut = () => {
		this.sectionsRef.current && this.sectionsRef.current.onCollapsedSectionClick(1);
	};

	getTimelineWidth = (sectionWidth: number): number => {
		const { isExportMode, exportModeTimelineWidth } = this.props;

		return isExportMode ? exportModeTimelineWidth : sectionWidth;
	};

	measureAndUpdateWidth = () => {
		const ref = this.getContainerRef().current;
		if (!ref) return;

		// Viewport width
		const { width: viewportWidth } = getViewportSize();
		let containerWidth: number = viewportWidth;

		const rightSideBarWidth = this.getRightSidebarWidth();

		containerWidth -= rightSideBarWidth;

		if (
			!this.state.timelineFullScreen &&
			!this.props.isExportMode &&
			!this.props.isConfluenceMacro &&
			!this.props.isEmbed &&
			this.props.leftSidebarWidth
		) {
			containerWidth -= this.props.isReportMode
				? LEFT_MARGIN_REPORT
				: this.props.leftSidebarWidth + LEFT_MARGIN_DEFAULT;
		}

		containerWidth = Math.max(containerWidth, MIN_APP_WIDTH);
		if (this.state.containerWidth !== containerWidth) {
			this.setState({
				containerWidth,
			});
		}
	};

	getRightSidebarWidth = () =>
		isDefined(this.props.rightSidebarWidth) && !this.state.timelineFullScreen
			? this.props.rightSidebarWidth
			: 0;

	measureAndUpdateWidthThrottled = throttle(this.measureAndUpdateWidth, 50);

	fieldsColumnTextsRenderer = ({ section }: SectionRendererArgs) => {
		const { viewMode } = this.props;

		const id = viewMode === VIEW_MODES.LIST ? SECTION_KEYS.LIST_FIELD : SECTION_KEYS.FIELD;
		const style = { width: section.width, overflow: 'hidden' };

		// if the section is not collapsed and is the fields column
		if (section.width > section.minWidth && section.id === SECTION_ITEM_KEYS.FIELDS) {
			return (
				// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
				<div style={style}>
					<Sections.ColumnTexts
						id={id}
						isExportMode={this.props.isExportMode}
						namespace={this.props.viewNamespace}
					/>
				</div>
			);
		}

		// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
		return <div style={style} />;
	};

	getSectionWidth = (section: SectionData) => {
		if (section.id === SECTION_ITEM_KEYS.TIMELINE) {
			return this.getTimelineWidth(section.width);
		}
		if (section.id === SECTION_ITEM_KEYS.FIELDS && this.props.viewMode === VIEW_MODES.LIST) {
			return this.fieldsViewportWidth;
		}
		return section.width;
	};

	sectionRenderer = ({
		section,
		onSectionClick,
		index,
		isDragging,
		hoveredSectionIndex,
		setHovered,
	}: SectionRendererArgs) => {
		// eslint-disable-next-line @typescript-eslint/no-shadow
		const { Fields, Scope, Timeline, leftSidebarWidth, viewMode, isExportMode } = this.props;
		const width = this.getSectionWidth(section);

		return (
			<Sections.Item
				id={section.id}
				key={`section-${section.id}`}
				index={index}
				width={width}
				minWidth={section.minWidth}
				onClick={onSectionClick}
				collapsible={section.collapsible}
				hoveredSectionIndex={hoveredSectionIndex}
				setHovered={setHovered}
			>
				{
					{
						[SECTION_ITEM_KEYS.SCOPE]: (
							<div
								// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
								className={`${styles[SECTION_ITEM_KEYS.SCOPE]}`}
							>
								{/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766 */}
								<div className={styles['scope-container']}>
									<Scope
										width={section.width}
										scrollElement={this.scrollElement}
										isDragging={isDragging}
									/>
								</div>
								{/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766 */}
								<div className={styles.space} />
							</div>
						),
						[SECTION_ITEM_KEYS.FIELDS]:
							viewMode === VIEW_MODES.LIST && !isExportMode ? (
								<HorizontalSyncedScrollView
									primary
									name="fields"
									width={section.width}
									minWidth={width}
								>
									<div
										// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
										className={`${styles[SECTION_ITEM_KEYS.FIELDS]}`}
										style={{
											minWidth: width,
										}}
									>
										<Fields
											width={section.width}
											minWidth={width}
											scrollElement={this.scrollElement}
											isDragging={isDragging}
										/>
									</div>
								</HorizontalSyncedScrollView>
							) : (
								<div
									// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
									className={`${styles[SECTION_ITEM_KEYS.FIELDS]}`}
									style={{
										minWidth: width,
									}}
								>
									<Fields
										width={section.width}
										scrollElement={this.scrollElement}
										isDragging={isDragging}
									/>
								</div>
							),
						[SECTION_ITEM_KEYS.TIMELINE]: (
							<div
								// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
								className={`${styles[SECTION_ITEM_KEYS.TIMELINE]}`}
							>
								<Timeline
									scrollElement={this.scrollElement}
									width={width}
									isDragging={isDragging}
									leftSidebarWidth={leftSidebarWidth}
								/>
							</div>
						),
					}[section.id]
				}
			</Sections.Item>
		);
	};

	horizontalScrollBarRenderer = ({ section }: SectionRendererArgs) => {
		const { isExportMode, viewMode } = this.props;

		if (section.id === SECTION_ITEM_KEYS.TIMELINE && !isExportMode) {
			return (
				<div
					style={{
						width: section.width,
						// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
						position: 'relative',
					}}
				>
					<HorizontalScrollBar />
				</div>
			);
		}

		if (viewMode === VIEW_MODES.LIST && section.id === SECTION_ITEM_KEYS.FIELDS && !isExportMode) {
			return (
				<div
					style={{
						width: this.fieldsViewportWidth,
						// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
						position: 'relative',
					}}
				>
					<HorizontalSyncedScrollbar name="fields" />
				</div>
			);
		}

		return <div style={{ width: section.width }} />;
	};

	collapse = (index: number) => () => {
		this.sectionsRef.current ? this.sectionsRef.current.collapseSection(index) : null;
	};

	stickyHeaderRenderer = ({
		section,
		onSectionClick,
		index,
		isDragging,
		hoveredSectionIndex,
		setHovered,
	}: SectionRendererArgs) => {
		const { viewMode, isExportMode } = this.props;
		const width = this.getSectionWidth(section);

		return (
			<Sections.Item
				id={section.id}
				key={`section-${section.id}`}
				index={index}
				width={width}
				minWidth={section.minWidth}
				onClick={onSectionClick}
				collapsible={section.collapsible}
				hoveredSectionIndex={hoveredSectionIndex}
				setHovered={setHovered}
			>
				{
					{
						[SECTION_ITEM_KEYS.SCOPE]: (
							<div
								// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
								className={`${styles[SECTION_ITEM_KEYS.SCOPE]}`}
							>
								<StickyHeaderCell
									containerId={`${STICKY_HEADER_CONTAINER_PREFIX}-scope`}
									isDragging={isDragging}
									width={section.width}
								/>
							</div>
						),
						[SECTION_ITEM_KEYS.TIMELINE]: (
							<div
								// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
								className={`${styles[SECTION_ITEM_KEYS.SCOPE]}`}
							>
								<StickyHeaderCell
									containerId={`${STICKY_HEADER_CONTAINER_PREFIX}-timeline`}
									isDragging={isDragging}
									width={width}
								/>
							</div>
						),
						[SECTION_ITEM_KEYS.FIELDS]:
							viewMode === VIEW_MODES.LIST && !isExportMode ? (
								<HorizontalSyncedScrollView name="fields" width={section.width} minWidth={width}>
									<div
										// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
										className={`${styles[SECTION_ITEM_KEYS.FIELDS]}`}
										style={{
											minWidth: width,
										}}
									>
										<StickyHeaderFields
											width={section.width}
											isDragging={isDragging}
											minWidth={width}
										/>
									</div>
								</HorizontalSyncedScrollView>
							) : (
								<div
									// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
									className={`${styles[SECTION_ITEM_KEYS.FIELDS]}`}
									style={{
										minWidth: width,
									}}
								>
									<StickyHeaderFields
										width={section.width}
										isDragging={isDragging}
										minWidth={undefined}
									/>
								</div>
							),
					}[section.id]
				}
			</Sections.Item>
		);
	};

	sectionHeadersRenderer = ({
		section,
		onSectionClick,
		index,
		isDragging,
		hoveredSectionIndex,
		setHovered,
	}: SectionRendererArgs) => {
		const {
			// eslint-disable-next-line @typescript-eslint/no-shadow
			FieldsHeader,
			// eslint-disable-next-line @typescript-eslint/no-shadow
			ScopeHeader,
			// eslint-disable-next-line @typescript-eslint/no-shadow
			TimelineHeader,
			// eslint-disable-next-line @typescript-eslint/no-shadow
			AllIssuesSelect,
			activeCard,
			viewMode,
			isExportMode,
		} = this.props;
		const { containerWidth } = this.state;

		const width = this.getSectionWidth(section);

		// While onboarding, if the sections are collapsed or too large, we expand or shrink them
		if (activeCard === 'update-jira' && shouldSetOnboardingWidth(section, containerWidth)) {
			this.sectionsRef.current && this.sectionsRef.current.setOnboardingWidth();
		}

		return (
			<Sections.Item
				id={section.id}
				key={`section-${section.id}`}
				index={index}
				width={width}
				minWidth={section.minWidth}
				title={section.title}
				onClick={onSectionClick}
				collapsible={section.collapsible}
				hoveredSectionIndex={hoveredSectionIndex}
				setHovered={setHovered}
			>
				{
					{
						[SECTION_ITEM_KEYS.SCOPE]: (
							<div
								// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
								className={`${styles[SECTION_ITEM_KEYS.SCOPE]}`}
							>
								<AllIssuesSelect isDragging={isDragging}>
									{(props: Props) => <ScopeHeader {...props} />}
								</AllIssuesSelect>
							</div>
						),
						[SECTION_ITEM_KEYS.FIELDS]:
							viewMode === VIEW_MODES.LIST && !isExportMode ? (
								<HorizontalSyncedScrollView name="fields" width={section.width} minWidth={width}>
									<div
										// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
										className={`${styles[SECTION_ITEM_KEYS.FIELDS]} ${styles['pointer-cursor']}`}
										style={{
											minWidth: width,
										}}
									>
										<FieldsHeader
											isDragging={isDragging}
											onCollapseAllFields={this.collapse(index)}
											width={section.width}
											minWidth={width}
										/>
									</div>
								</HorizontalSyncedScrollView>
							) : (
								<div
									// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
									className={`${styles[SECTION_ITEM_KEYS.FIELDS]} ${styles['pointer-cursor']}`}
									style={{
										minWidth: width,
									}}
								>
									<FieldsHeader
										isDragging={isDragging}
										onCollapseAllFields={this.collapse(index)}
										width={section.width}
									/>
								</div>
							),
						[SECTION_ITEM_KEYS.TIMELINE]: (
							<div
								// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
								className={`${styles[SECTION_ITEM_KEYS.TIMELINE]} ${
									styles['timeline-column-header']
								}`}
								aria-hidden
							>
								<TimelineHeader width={width} />
							</div>
						),
					}[section.id]
				}
			</Sections.Item>
		);
	};

	sendViewRoadmapScreenAnalyticsEvent = (analyticsEvent: UIAnalyticsEvent) => {
		this.props.onRoadmapView(analyticsEvent);
	};

	render() {
		const {
			// eslint-disable-next-line @typescript-eslint/no-shadow
			ZoomLevelSync,
			// eslint-disable-next-line @typescript-eslint/no-shadow
			TimeScale,
			// eslint-disable-next-line @typescript-eslint/no-shadow
			PanningControl,
			// eslint-disable-next-line @typescript-eslint/no-shadow
			IssueModal,
			// eslint-disable-next-line @typescript-eslint/no-shadow
			ViewExperienceFailureTracker,
			scopeHeight,
			isOptimizedMode,
			activeCard,
			stickyHeader,
			hasWarningInConfiguredDates,
			viewNamespace,
			isExportMode,
			zoomLevel,
			spotlight,
			intl,
			edition,
			isConfluenceMacro,
			isEmbed,
			isReportMode,
			shouldDisplayTmpCrossProjectChangeboarding,
			hierarchyTitles,
			viewMode,
			isLoading,
		} = this.props;

		const mainSectionsData = getSectionsData(intl, viewMode);

		const { containerWidth, timelineFullScreen } = this.state;
		const id = viewMode === VIEW_MODES.LIST ? SECTION_KEYS.LIST_MAIN : SECTION_KEYS.MAIN;

		const toolbarItems: ToolbarItem[] = [
			{
				id: 'pan-left-right',
				element: (
					<>
						<PanningControl direction="left" />
						<PanningControl direction="right" />
					</>
				),
				isVisible: Boolean(zoomLevel && viewMode === VIEW_MODES.TIMELINE),
			},
			{
				id: 'today-button',
				element: <TodayButton />,
				isVisible: Boolean(zoomLevel && viewMode === VIEW_MODES.TIMELINE),
			},
			{
				id: 'timeline-mode-switcher',
				element: <TimeScale />,
				isVisible: viewMode === VIEW_MODES.TIMELINE,
			},
			{
				id: 'legend',
				element: <Legend />,
				isVisible: true,
			},
			{
				id: 'fullscreen-button',
				element: (
					<FullscreenButton
						testId="portfolio-3-portfolio.app-simple-plans.main.tabs.roadmap.fullscreen"
						onToggle={this.toggleFullscreen}
						analyticsAttributes={{
							screenWidth: window.innerWidth,
							screenHeight: window.innerHeight,
						}}
						withContainerStyles={false}
					/>
				),
			},
		];

		let content = (
			<div
				ref={this.getContainerRef()}
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
				className={`${styles.container} ${
					this.props.spotlight === Spotlights.HeartOfPlan ||
					this.props.spotlight === CttSpotlights.Scope
						? styles.highlight
						: ''
				}`}
			>
				{(this.props.spotlight === Spotlights.HeartOfPlan ||
					this.props.spotlight === CttSpotlights.Scope) && (
					<OnboardingSpotlightTarget name={this.props.spotlight}>
						<div
							aria-describedby="Spotlight Anchor"
							style={{
								// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
								position: 'absolute',
								// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
								top: 0,
								// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
								bottom: 0,
								zIndex: -1,
								// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
								width: '100%',
							}}
						/>
					</OnboardingSpotlightTarget>
				)}
				<DependenciesFlyoutOverlay />
				<UseShortcuts shortcutKey="KeyA" onKeyDown={this.expandFieldsShortcut} />
				{!isLoading && <UseLocalStorageCommand />}
				<Sections.Provider ref={this.providerRef}>
					{!isExportMode && (
						<UFOSegment name="view-advanced-roadmaps-timeline-overlay">
							<JiraBottomRightCornerOutlet
								orderFromRight={2}
								id="portfolio-3-plans-timeline-overlay"
							>
								<Toolbar items={toolbarItems} isCollapsible={viewMode === VIEW_MODES.TIMELINE} />
							</JiraBottomRightCornerOutlet>
						</UFOSegment>
					)}
					<FocusLock
						disabled={!timelineFullScreen || isDefined(this.props.issueBeingEdited)}
						noFocusGuards
						autoFocus={false}
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
						className={styles.mainSectionsWrap}
						// @ts-expect-error - TS2322 - Type '(node: HTMLElement) => boolean | undefined' is not assignable to type '(activeElement: HTMLElement) => boolean'.
						whiteList={(node) =>
							node.id === 'shifter-dialog' ||
							document.querySelector('.atlaskit-portal-container')?.contains(node)
						}
					>
						{/* @ts-expect-error - TS2322 - Type '{ children: (false | Element)[]; id: "main-sections"; "data-testid": string; styleName: string; namespace: string; }' is not assignable to type 'IntrinsicAttributes & NamespacedIdProps'. */}
						<Sections.Container
							id={id}
							data-testid="portfolio-3-portfolio.app-simple-plans.main.tabs.roadmap.roadmap"
							// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
							className={`${styles.mainSections} ${timelineFullScreen ? styles.fullScreen : ''}`}
							namespace={viewNamespace}
							width={containerWidth}
						>
							{viewMode !== VIEW_MODES.LIST && (
								// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
								<div className={styles.columnsTexts}>
									<Sections.ColumnTexts
										id={id}
										isExportMode={isExportMode}
										namespace={viewNamespace}
									/>
								</div>
							)}
							{/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766 */}
							<div className={styles['content-header']}>
								<Sections
									id={id}
									namespace={viewNamespace}
									// @ts-expect-error - TS2322 - Type '{ id: "main-sections"; namespace: string; sectionRenderer: ({ section, onSectionClick, index, isDragging, hoveredSectionIndex, setHovered, }: SectionRendererArgs) => Element; sectionsData: never[]; onDragStart: () => void; onDragEnd: () => void; width: number; activeCard: string; }' is not assignable to type 'IntrinsicAttributes & NamespacedIdProps'.
									sectionRenderer={this.sectionHeadersRenderer}
									sectionsData={mainSectionsData}
									onDragStart={this.props.onDragStart}
									onDragEnd={this.props.onDragEnd}
									width={containerWidth}
									activeCard={activeCard}
									spotlight={spotlight}
									// Call handleSectionsRender when the FIRST Sections renders,
									// it sets the columns widths that required by Sections.Item
									onRender={this.handleSectionsRender}
								/>
							</div>
							{/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766 */}
							<div className={styles['content-row']}>
								{/* We require to pass DOM node of ScrollContainer div to its children,
            Scope, Timeline and Fields. Accessing ref via a callback function doesn't
            guarantee that it will be available when children will get rendered.
            In future, find a right fix for to access parent DOM node and pass it to
            its children.  */}
								{stickyHeader?.height ? (
									// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop, @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
									<div className={styles['sticky-headers']} style={{ minHeight: 40 }}>
										<Sections
											id={id}
											namespace={viewNamespace}
											// @ts-expect-error - TS2322 - Type '{ id: "main-sections"; namespace: string; minHeight: number; sectionRenderer: ({ section, onSectionClick, index, isDragging, hoveredSectionIndex, setHovered, }: SectionRendererArgs) => Element; sectionsData: never[]; onDragStart: () => void; onDragEnd: () => void; width: number; activeCard: string; height: number; }' is not assignable to type 'IntrinsicAttributes & NamespacedIdProps'.
											minHeight={40}
											sectionRenderer={this.stickyHeaderRenderer}
											sectionsData={mainSectionsData}
											onDragStart={this.props.onDragStart}
											onDragEnd={this.props.onDragEnd}
											width={containerWidth}
											activeCard={activeCard}
											spotlight={spotlight}
											height={stickyHeader?.height || 0}
										/>
									</div>
								) : null}
								{viewMode !== VIEW_MODES.LIST && (
									// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
									<div className={styles.columnsTexts}>
										<Sections
											id={id}
											namespace={viewNamespace}
											// @ts-expect-error - TS2322 - Type '{ id: "main-sections"; namespace: string; sectionsData: never[]; sectionRenderer: ({ section }: SectionRendererArgs) => Element; width: number; onRender: (sectionsData: SectionsData) => void; }' is not assignable to type 'IntrinsicAttributes & NamespacedIdProps'.
											sectionsData={mainSectionsData}
											sectionRenderer={this.fieldsColumnTextsRenderer}
											width={containerWidth}
											spotlight={spotlight}
										/>
									</div>
								)}
								<ContentScroller
									id="portfolio-roadmap-table-content-scroller"
									data-scroll="lockable"
									// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
									className={
										isExportMode ? styles['content-scroller-export'] : styles['content-scroller']
									}
									onScroll={throttle(this.handleScroll, 16)}
									ref={(node: HTMLDivElement) => {
										this.scrollElement = node;
									}}
								>
									<div
										ref={this.sectionsContainerRef}
										// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
										className={`${styles.contentArea} ${isOptimizedMode ? styles.optimized : ''}`}
										// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
										style={{ height: scopeHeight }}
									>
										<Sections
											// @ts-expect-error - TS2322 - Type '{ innerRef: RefObject<any>; minHeight: string; id: "main-sections"; namespace: string; sectionRenderer: ({ section, onSectionClick, index, isDragging, hoveredSectionIndex, setHovered, }: SectionRendererArgs) => Element; ... 5 more ...; activeCard: string; }' is not assignable to type 'IntrinsicAttributes & NamespacedIdProps'.
											innerRef={this.sectionsRef}
											minHeight="100%"
											id={id}
											namespace={viewNamespace}
											sectionRenderer={this.sectionRenderer}
											sectionsData={mainSectionsData}
											onDragStart={this.props.onDragStart}
											onDragEnd={this.props.onDragEnd}
											width={containerWidth}
											height={scopeHeight}
											activeCard={activeCard}
											spotlight={spotlight}
										/>
									</div>
								</ContentScroller>
							</div>
							<HorizontalScrollBarOverlay />
							{/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766 */}
							<div className={styles.horizontalScrollbar}>
								<Sections
									id={id}
									namespace={viewNamespace}
									// @ts-expect-error - TS2322 - Type '{ id: "main-sections"; namespace: string; sectionsData: never[]; sectionRenderer: ({ section }: SectionRendererArgs) => Element; width: number; }' is not assignable to type 'IntrinsicAttributes & NamespacedIdProps'.
									sectionsData={mainSectionsData}
									sectionRenderer={this.horizontalScrollBarRenderer}
									width={containerWidth}
								/>
							</div>
						</Sections.Container>
					</FocusLock>

					{hasWarningInConfiguredDates && (
						<FlagGroup>
							<ConfiguredDatesFlag />
						</FlagGroup>
					)}
				</Sections.Provider>
				<IssueModal />
			</div>
		);

		content = <DependenciesFlyoutProvider>{content}</DependenciesFlyoutProvider>;
		content = (
			<>
				{content}
				<ZoomLevelSync />
				{shouldDisplayTmpCrossProjectChangeboarding && (
					<TmpCrossProjectChangeBoarding hierarchyTitles={hierarchyTitles} />
				)}
			</>
		);

		return (
			<>
				<UFOSegment name="view-advanced-roadmaps-timeline">
					<ViewExperienceTrackingProvider
						experience={getExperienceName(isReportMode, isConfluenceMacro, isEmbed)}
						analyticsSource="portfolio3"
						application={JIRA_PORTFOLIO}
						edition={edition}
						additionalAttributes={{
							synthetic: isPollinatorTenant(),
						}}
					>
						<ViewExperienceFailureTracker
							location="portfolio-3.simple-plans.roadmap.state-or-global-error"
							locationType="path"
							locationPrefix="roadmap"
						/>
						<ContextualAnalyticsData
							sourceType={SCREEN}
							sourceName={ERROR_REPORTING_PACKAGE.ROADMAP}
						>
							<MountEvent onMount={this.sendViewRoadmapScreenAnalyticsEvent} />
							<ReportErrors
								id={ERROR_REPORTING_PACKAGE.ROADMAP}
								packageName={PACKAGE_NAME}
								teamName={ERROR_REPORTING_TEAM}
								sendToPrivacyUnsafeSplunk
							>
								{content}
							</ReportErrors>
						</ContextualAnalyticsData>
					</ViewExperienceTrackingProvider>
					<UFOCustomData data={{ viewMode }} />
				</UFOSegment>
				{ff('spork-in-product-survey') && <RatingFeedbackCollectorAsync />}
			</>
		);
	}
}

export default Roadmap;
